import { useContext } from 'react';
import type { FC, ComponentType } from 'react';
import track from 'react-tracking';
import SWSLogo from '@assets/images/logo/sws-logo-updated.svg';
import { ROUTE_HOME, ROUTE_DASHBOARD } from '@/constants/routes';
import { RouterLink } from '@redux-first-router-link';
import { Row, Column } from '@simplywallst/ui-core';
import loadable from '@loadable/component';
import {
  DesktopTopbarBound,
  DesktopTopbarWrapper,
  SWSLogoWrapper,
  MainNavigation,
  SearchWrapper,
} from './styled';
import { PlanType } from '@/components/OurPlans/components/Plans/constants';
import useUser from '@/hooks/useUser';
import { useUserAccess } from '@/hooks/useUserAccess';
import LayoutContext from '@/components/Layout/context/LayoutContext/LayoutContext';

const Nav = loadable(() => import('./components/Nav'), {
  resolveComponent: (component) => component.Nav,
});

const BasicSearch = loadable(
  () => import('@components/Search/components/BasicSearch'),
  { ssr: false }
);

const DesktopTopbarAccountAccess = loadable(
  () =>
    import('@components/DesktopTopbar/components/DesktopTopbarAccountAccess')
);

const DesktopTopbarLoggedOut = loadable(
  () => import('@components/DesktopTopbar/components/DesktopTopbarLoggedOut')
);

const TrialNotification = loadable(() => import('../TrialNotification'), {
  ssr: false,
});
export interface Props {
  hasPaymentDetails: boolean;
  location: string;
  daysUntilTrialEnds: string;
}

const DesktopTopbar: FC<Props & Tracking> = ({
  location,
  tracking: { trackEvent },
  daysUntilTrialEnds,
  hasPaymentDetails,
}) => {
  const layout = useContext(LayoutContext);
  const user = useUser();
  const access = useUserAccess();
  const isTrialing = user.data.billing.status === 'trialing';
  const canShowTrialNotifications =
    user.isFetched &&
    user.data.loggedIn &&
    ((access.status === 'success' &&
      access.data.viewCompanyReports !== -1 &&
      access.data.planType === PlanType.Free) ||
      isTrialing);

  return (
    <DesktopTopbarWrapper
      as="header"
      justifyContent="center"
      data-cy-id="main-navigation-wrapper"
    >
      <DesktopTopbarBound wide={layout.wider}>
        <SWSLogoWrapper>
          <RouterLink
            to={{ type: ROUTE_HOME }}
            onClick={() =>
              location !== ROUTE_DASHBOARD &&
              trackEvent({
                action: 'click',
                modifier: 'logo',
              })
            }
          >
            <SWSLogo />
          </RouterLink>
        </SWSLogoWrapper>
        <Row as="nav">
          <MainNavigation data-cy-id="main-navigation">
            <Nav location={location} />
            <SearchWrapper>
              <BasicSearch />
            </SearchWrapper>
          </MainNavigation>
          {canShowTrialNotifications ? (
            <Column width="auto" alignSelf="center">
              <TrialNotification
                location={location}
                planType={access.data.planType}
                isTrialing={isTrialing}
                daysUntilTrialEnds={daysUntilTrialEnds}
              />
            </Column>
          ) : null}
          {user.isFetched ? (
            user.data.loggedIn ? (
              <DesktopTopbarAccountAccess
                location={location}
                maxViewCompanyReports={
                  access.status === 'success'
                    ? access.data.viewCompanyReports
                    : 0
                }
                hasPaymentDetails={hasPaymentDetails}
              />
            ) : (
              <DesktopTopbarLoggedOut />
            )
          ) : null}
        </Row>
      </DesktopTopbarBound>
    </DesktopTopbarWrapper>
  );
};

export default track({
  type: 'track',
  subject: 'primaryNavDesktop',
})(DesktopTopbar) as ComponentType<Props>;
